import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { StockQuoteDetail } from '@/components/ui/StockQuote';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { useChains } from '@/hooks/queries/useChains';
import { useQuotes } from '@/hooks/queries/useQuotes';
import { useQuery } from '@tanstack/react-query';
import { stockApi, type StockPosition } from '@/lib/api/client';
import { TradeSuggestions } from '@/components/TradeSuggestions';
import { SymbolPremiumFlow } from '@/components/analytics/SymbolPremiumFlow';
import { JournalFeed } from '@/components/journal/JournalFeed';
import { formatDate, formatCurrency, getDaysToExpiration, formatDTE } from '@/lib/formatters';
import { cn } from '@/lib/utils';

function originLabel(origin: string) {
  switch (origin) {
    case 'assignment': return 'Assignment';
    case 'exercise': return 'Exercise';
    case 'direct_purchase': return 'Purchase';
    default: return origin;
  }
}

function daysHeld(openedAt: Date | string, closedAt: Date | string | null): number {
  const start = new Date(openedAt).getTime();
  const end = closedAt ? new Date(closedAt).getTime() : Date.now();
  return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
}

function StockHoldingBanner({
  totalShares,
  avgCostBasis,
  totalCostBasis,
  marketValue,
  unrealizedPnl,
  positions,
}: {
  totalShares: number;
  avgCostBasis: number;
  totalCostBasis: number;
  marketValue: number | null;
  unrealizedPnl: number | null;
  positions: StockPosition[];
}) {
  const [expanded, setExpanded] = useState(false);
  const openPositions = positions.filter(p => p.status === 'open');

  return (
    <Card className="border-blue-200 bg-blue-50">
      <div
        className="flex flex-wrap items-center justify-between gap-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <svg
            className={cn('w-4 h-4 text-blue-400 transition-transform', expanded && 'rotate-90')}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-blue-900">
              Holding {totalShares.toLocaleString()} shares
            </p>
            <p className="text-xs text-blue-600">
              Avg cost ${avgCostBasis.toFixed(2)} · Basis {formatCurrency(totalCostBasis)}
            </p>
          </div>
        </div>
        {marketValue !== null && unrealizedPnl !== null && (
          <div className="text-right">
            <p className="text-sm font-mono text-blue-900">
              {formatCurrency(marketValue)}
            </p>
            <p className={cn(
              'text-xs font-mono font-medium',
              unrealizedPnl >= 0 ? 'text-green-700' : 'text-red-700'
            )}>
              {unrealizedPnl >= 0 ? '+' : ''}{formatCurrency(unrealizedPnl)}
              {' '}({((unrealizedPnl / totalCostBasis) * 100).toFixed(1)}%)
            </p>
          </div>
        )}
      </div>
      {expanded && openPositions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-blue-200 space-y-2">
          <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
            Stock Lots ({openPositions.length})
          </h4>
          {openPositions.map((pos) => (
            <div key={pos.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 bg-blue-100/50 rounded-lg px-3 py-2 text-sm">
              <div className="flex items-center gap-3">
                <span className="font-mono font-medium text-blue-900">
                  {pos.quantityRemaining.toLocaleString()} shares
                </span>
                <span className="text-xs text-blue-700">
                  @ ${pos.costBasisPerShare.toFixed(2)}
                </span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-200 text-blue-800">
                  {originLabel(pos.origin)}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-blue-600">
                <span>Acquired {formatDate(pos.openedAt)}</span>
                <span className="font-medium text-blue-800">{daysHeld(pos.openedAt, pos.closedAt)}d held</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function SymbolChainsPage() {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const { data: chains, isLoading } = useChains({ symbol });
  const { data: quotes, isLoading: quotesLoading } = useQuotes(symbol ? [symbol] : []);
  const { data: stockPositions } = useQuery({
    queryKey: ['stock-positions', symbol],
    queryFn: () => stockApi.getBySymbol(symbol!),
    enabled: !!symbol,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const quote = symbol ? quotes?.[symbol] : null;

  // Calculate stock holding totals
  const totalShares = stockPositions?.reduce((sum, sp) => sum + sp.quantityRemaining, 0) ?? 0;
  const totalCostBasis = stockPositions?.reduce((sum, sp) => sum + sp.quantityRemaining * sp.costBasisPerShare, 0) ?? 0;
  const avgCostBasis = totalShares > 0 ? totalCostBasis / totalShares : 0;
  const marketValue = quote?.price ? quote.price * totalShares : null;
  const unrealizedPnl = marketValue !== null ? marketValue - totalCostBasis : null;

  const hasChains = chains && chains.length > 0;

  if (!hasChains && totalShares === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">{symbol}</h1>
          <Link to="/chains">
            <Button variant="secondary">Back to Chains</Button>
          </Link>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No trade chains or positions found for {symbol}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate aggregate stats
  const allChains = chains ?? [];
  const openChains = allChains.filter(c => c.status === 'open' || c.status === 'partial');
  const closedChains = allChains.filter(c => c.status === 'closed');
  const totalRealizedPnl = closedChains.reduce((sum, c) => sum + c.realizedPnl, 0);
  const totalPremiumCollected = allChains.reduce((sum, c) => sum + c.totalPremiumCollected, 0);
  const totalPremiumPaid = allChains.reduce((sum, c) => sum + c.totalPremiumPaid, 0);
  const winCount = closedChains.filter(c => c.realizedPnl > 0).length;
  const winRate = closedChains.length > 0 ? (winCount / closedChains.length) * 100 : 0;

  // Get earliest expiration from open positions
  const getEarliestExpiration = (chain: typeof allChains[0]) => {
    let earliest: Date | null = null;
    for (const position of chain.positions) {
      if (position.status !== 'open') continue;
      for (const leg of position.legs) {
        const exp = new Date(leg.expirationDate);
        if (!earliest || exp < earliest) {
          earliest = exp;
        }
      }
    }
    return earliest;
  };

  return (
    <div className="space-y-6">
      {/* Sticky Header — sits below the main app header (top ~49px) */}
      <div className="sticky top-[49px] z-[5] bg-white/95 backdrop-blur-sm -mx-4 px-4 py-3 border-b border-gray-100 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{symbol}</h1>
            {quote && !quotesLoading && (
              <span className="text-lg text-gray-600">${quote.price.toFixed(2)}</span>
            )}
            <span className="text-sm text-gray-400">
              {allChains.length} chain{allChains.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/transactions?symbol=${symbol}`}>
              <Button variant="ghost" size="sm">Transactions</Button>
            </Link>
            <Link to="/chains">
              <Button variant="secondary" size="sm">Back to Chains</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Current Price */}
      <Card>
        <CardHeader>
          <CardTitle>Current Price</CardTitle>
        </CardHeader>
        <CardContent>
          <StockQuoteDetail
            price={quote?.price ?? null}
            change={quote?.change ?? null}
            changePercent={quote?.changePercent ?? null}
            isLoading={quotesLoading}
          />
        </CardContent>
      </Card>

      {/* Stock Holding Banner */}
      {totalShares > 0 && (
        <StockHoldingBanner
          totalShares={totalShares}
          avgCostBasis={avgCostBasis}
          totalCostBasis={totalCostBasis}
          marketValue={marketValue}
          unrealizedPnl={unrealizedPnl}
          positions={stockPositions ?? []}
        />
      )}

      {/* Summary Stats */}
      {hasChains && <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Open Chains</p>
          <p className="text-xl font-semibold text-gray-900">{openChains.length}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Closed Chains</p>
          <p className="text-xl font-semibold text-gray-900">{closedChains.length}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Total Realized P&L</p>
          <PnlDisplay value={totalRealizedPnl} size="lg" />
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Net Premium</p>
          <p className={cn('text-lg font-semibold', totalPremiumCollected - totalPremiumPaid >= 0 ? 'text-green-600' : 'text-red-600')}>
            {formatCurrency(totalPremiumCollected - totalPremiumPaid)}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Win Rate</p>
          <p className="text-xl font-semibold text-gray-900">
            {closedChains.length > 0 ? `${winRate.toFixed(0)}%` : '-'}
          </p>
        </Card>
      </div>}

      {symbol && <SymbolPremiumFlow symbol={symbol} />}

      {symbol && <JournalFeed symbol={symbol} title="Journal" />}

      {/* Trade Suggestions */}
      <TradeSuggestions symbol={symbol!} sharesHeld={totalShares} />

      {/* Chains Table */}
      {hasChains &&
      <Card>
        <CardHeader>
          <CardTitle>Trade Chains</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Strategy</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Positions</TableHead>
                <TableHead>Opened</TableHead>
                <TableHead>Closed</TableHead>
                <TableHead>Expiration</TableHead>
                <TableHead className="text-right">Net Premium</TableHead>
                <TableHead className="text-right">Realized P&L</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allChains.map((chain) => {
                const expiration = getEarliestExpiration(chain);
                const dte = expiration ? getDaysToExpiration(expiration) : null;
                const netPremium = chain.totalPremiumCollected - chain.totalPremiumPaid;
                const posCount = chain.positions?.length || 0;

                return (
                  <TableRow
                    key={chain.id}
                    className="cursor-pointer"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('a, button')) return;
                      navigate(`/chains/${chain.id}`);
                    }}
                  >
                    <TableCell>
                      <StrategyBadge strategy={chain.strategyType} size="sm" />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={chain.status} size="sm" />
                    </TableCell>
                    <TableCell className="text-center text-gray-600">
                      {posCount}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(chain.openedAt)}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {chain.closedAt ? formatDate(chain.closedAt) : '-'}
                    </TableCell>
                    <TableCell>
                      {expiration && dte !== null ? (
                        <span className={cn(
                          'text-sm',
                          dte <= 7 && dte >= 0 ? 'text-yellow-600 font-medium' : 'text-gray-600'
                        )}>
                          {formatDTE(expiration)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={cn('font-mono text-sm', netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
                        {formatCurrency(netPremium)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {chain.status === 'closed' ? (
                        <PnlDisplay value={chain.realizedPnl} size="sm" />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link to={`/chains/${chain.id}`}>
                        <Button variant="ghost" size="sm">View</Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>}
    </div>
  );
}
